import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import CityAutocomplete from "@/components/CityAutocomplete";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, X, ArrowUpDown, Compass } from "lucide-react";

const CAT_KEYS = ["ghar", "plot", "shop", "jamin"];

const DIRECTIONS = [
  { key: "N",  en: "North",       hi: "उत्तर",         icon: "↑" },
  { key: "NE", en: "North-East",  hi: "ईशान (उत्तर-पूर्व)", icon: "↗" },
  { key: "E",  en: "East",        hi: "पूर्व",         icon: "→" },
  { key: "SE", en: "South-East",  hi: "आग्नेय",        icon: "↘" },
  { key: "S",  en: "South",       hi: "दक्षिण",       icon: "↓" },
  { key: "SW", en: "South-West",  hi: "नैऋत्य",        icon: "↙" },
  { key: "W",  en: "West",        hi: "पश्चिम",       icon: "←" },
  { key: "NW", en: "North-West",  hi: "वायव्य",        icon: "↖" },
];

const SORT_OPTIONS = [
  { key: "newest", en: "Newest first", hi: "नए पहले" },
  { key: "price_asc", en: "Price: Low → High", hi: "क़ीमत: कम → अधिक" },
  { key: "price_desc", en: "Price: High → Low", hi: "क़ीमत: अधिक → कम" },
  { key: "views", en: "Most viewed", hi: "सबसे ज़्यादा देखे गए" },
  { key: "oldest", en: "Oldest first", hi: "पुराने पहले" },
];

export default function Listings() {
  const [params, setParams] = useSearchParams();
  const { t, lang, setLangByState } = useLang();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "all");
  const [city, setCity] = useState(params.get("city") || "");
  const [state, setState] = useState(params.get("state") || "");
  const [minP, setMinP] = useState(params.get("min_price") || "");
  const [maxP, setMaxP] = useState(params.get("max_price") || "");
  const [facing, setFacing] = useState(params.get("facing_direction") || "any");
  const [sort, setSort] = useState(params.get("sort") || "newest");

  const load = (sortValue = sort) => {
    setLoading(true);
    const p = {};
    if (q) p.q = q;
    if (category && category !== "all") p.category = category;
    if (city) p.city = city;
    if (state) p.state = state;
    if (minP) p.min_price = minP;
    if (maxP) p.max_price = maxP;
    if (facing && facing !== "any") p.facing_direction = facing;
    if (sortValue) p.sort = sortValue;
    api.get("/properties", { params: p })
      .then(({ data }) => setProperties(data))
      .catch(() => setProperties([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const apply = (e) => {
    e?.preventDefault();
    const next = {};
    if (q) next.q = q;
    if (category && category !== "all") next.category = category;
    if (city) next.city = city;
    if (state) next.state = state;
    if (minP) next.min_price = minP;
    if (maxP) next.max_price = maxP;
    if (facing && facing !== "any") next.facing_direction = facing;
    if (sort && sort !== "newest") next.sort = sort;
    setParams(next);
    if (state) setLangByState(state);
    load();
  };

  const reset = () => {
    setQ(""); setCategory("all"); setCity(""); setState(""); setMinP(""); setMaxP(""); setFacing("any"); setSort("newest");
    setParams({});
    setTimeout(() => load("newest"), 0);
  };

  const changeSort = (v) => {
    setSort(v);
    const next = Object.fromEntries(params.entries());
    if (v === "newest") delete next.sort; else next.sort = v;
    setParams(next);
    load(v);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-6">
        <div className="overline text-secondary">{t.nav.browse}</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">
          {lang === "hi" ? "सभी प्रॉपर्टीज़" : "All Properties"}
        </h1>
      </div>

      <form onSubmit={apply} className="bg-card border border-border rounded-xl p-4 sm:p-5 grid grid-cols-1 md:grid-cols-6 gap-3 shadow-sm mb-4">
        <div className="md:col-span-2 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t.filters.search}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            data-testid="filter-search"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="filter-category"><SelectValue placeholder={t.filters.all} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.filters.all}</SelectItem>
            {CAT_KEYS.map((c) => <SelectItem key={c} value={c}>{t.categories[c]}</SelectItem>)}
          </SelectContent>
        </Select>
        <CityAutocomplete
          value={city}
          onChange={setCity}
          onSelect={(it) => { setCity(it.city); if (it.state) setState(it.state); }}
          placeholder={t.filters.city}
          dataTestId="filter-city"
          state={state}
        />
        <Input placeholder={t.filters.state} value={state} onChange={(e) => setState(e.target.value)} data-testid="filter-state" />
        <div className="flex gap-2">
          <Input placeholder={t.filters.minPrice} type="number" value={minP} onChange={(e) => setMinP(e.target.value)} data-testid="filter-min" />
          <Input placeholder={t.filters.maxPrice} type="number" value={maxP} onChange={(e) => setMaxP(e.target.value)} data-testid="filter-max" />
        </div>
        {/* Vastu direction filter */}
        <div className="md:col-span-2 relative">
          <Compass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary z-10 pointer-events-none" />
          <Select value={facing} onValueChange={setFacing}>
            <SelectTrigger className="pl-9" data-testid="filter-facing">
              <SelectValue placeholder={lang === "hi" ? "किसी भी दिशा" : "Any facing direction"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{lang === "hi" ? "किसी भी दिशा (Vastu)" : "Any facing direction (Vastu)"}</SelectItem>
              {DIRECTIONS.map((d) => (
                <SelectItem key={d.key} value={d.key} data-testid={`facing-option-${d.key}`}>
                  <span className="mr-1.5">{d.icon}</span>
                  {lang === "hi" ? d.hi : d.en}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-6 flex gap-2 justify-end">
          <Button type="button" variant="outline" onClick={reset} data-testid="filter-reset"><X size={14} className="mr-1" />{t.filters.reset}</Button>
          <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="filter-apply"><Search size={14} className="mr-1" />{t.filters.apply}</Button>
        </div>
      </form>

      {/* Sort bar */}
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap" data-testid="results-count">
          <span>
            {loading
              ? (lang === "hi" ? "लोड हो रहा है…" : "Loading…")
              : `${properties.length} ${lang === "hi" ? "प्रॉपर्टी" : "properties"}`}
          </span>
          {facing !== "any" && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary/15 border border-secondary/40 text-secondary font-semibold text-[11px]" data-testid="active-facing-chip">
              <Compass size={10} />
              {DIRECTIONS.find((d) => d.key === facing)?.icon} {lang === "hi" ? DIRECTIONS.find((d) => d.key === facing)?.hi : DIRECTIONS.find((d) => d.key === facing)?.en}
              <button
                type="button"
                onClick={() => { setFacing("any"); setTimeout(() => { const next = Object.fromEntries(params.entries()); delete next.facing_direction; setParams(next); load(); }, 0); }}
                className="ml-1 hover:opacity-70"
                aria-label="clear facing filter"
              ><X size={10} /></button>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ArrowUpDown size={14} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">{lang === "hi" ? "क्रम" : "Sort by"}</span>
          <Select value={sort} onValueChange={changeSort}>
            <SelectTrigger className="h-9 w-48" data-testid="filter-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.key} value={o.key} data-testid={`sort-option-${o.key}`}>
                  {lang === "hi" ? o.hi : o.en}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="animate-pulse rounded-xl border border-border bg-card overflow-hidden">
              <div className="aspect-[4/3] bg-muted" />
              <div className="p-4 space-y-2">
                <div className="h-5 bg-muted rounded w-3/4" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : properties.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-xl bg-accent/30" data-testid="listings-empty">
          <p className="text-lg font-medium">{t.empty.noResults}</p>
          <p className="text-sm text-muted-foreground mt-1">{t.empty.tryDifferent}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="listings-grid">
          {properties.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
